/**
 * useChatNode - Hook for managing ChatNode state with Yjs collaboration
 *
 * Handles:
 * - Message storage in Yjs array for real-time collaboration
 * - Streaming responses from API via SSE
 * - Pin-to-canvas functionality (creates BlockNode from message)
 * - Output streaming to connected BlockNode
 */

import { useState, useCallback, useRef, useMemo, useSyncExternalStore } from 'react';
import type { Node, Edge } from '@xyflow/react';
import type * as Y from 'yjs';
import type { CanvasChatMessage, ChatNodeStatus, ChatNodeModelSettings, ChatImageAttachment } from '@deep-agents/shared-types';
import { createChatMessage } from '@deep-agents/shared-types';
import { useOutputNode } from './useOutputNode';
import { createOutputNode } from '../utils/createOutputNode';

// ============================================================================
// TYPES
// ============================================================================

export interface UseChatNodeOptions {
  /** ID of this ChatNode */
  nodeId: string;

  /** Board ID for API calls */
  boardId: string;

  /** Yjs map of nodes */
  nodesMap: Y.Map<Node>;

  /** Yjs map of edges */
  edgesMap: Y.Map<Edge>;

  /** Yjs document for collaboration */
  ydoc: Y.Doc;

  /** Wrapper for undo/redo support */
  makeChange: (fn: () => void) => void;

  /** Model settings (temperature, model, system instructions) */
  modelSettings?: ChatNodeModelSettings;
}

export interface UseChatNodeResult {
  /** Array of all chat messages (from Yjs) */
  messages: CanvasChatMessage[];

  /** Current node status */
  status: ChatNodeStatus;

  /** Error message if status is 'error' */
  error: string | null;

  /** Whether output port is connected to a BlockNode */
  isOutputConnected: boolean;

  /** Send a new message and get AI response (with optional images) */
  sendMessage: (content: string, images?: ChatImageAttachment[]) => Promise<void>;

  /** Pin a message to canvas as a new BlockNode */
  pinMessage: (messageId: string) => void;

  /** Clear all chat history */
  clearChat: () => void;

  /** Stop current streaming response */
  stop: () => void;
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

/**
 * Get Yjs array key for chat messages
 */
function getChatArrayKey(nodeId: string): string {
  return `chat-${nodeId}`;
}

/**
 * Subscribe to Yjs array changes
 */
function createYArrayStore<T>(yarray: Y.Array<T>) {
  return {
    subscribe: (callback: () => void) => {
      yarray.observe(callback);
      return () => yarray.unobserve(callback);
    },
    getSnapshot: () => yarray.toArray(),
  };
}

export function useChatNode(options: UseChatNodeOptions): UseChatNodeResult {
  const { nodeId, boardId, nodesMap, edgesMap, ydoc, makeChange, modelSettings } = options;

  // State
  const [status, setStatus] = useState<ChatNodeStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  // Abort controller for cancelling in-flight requests
  const abortControllerRef = useRef<AbortController | null>(null);

  // Get or create the Yjs array for messages
  const chatArrayKey = getChatArrayKey(nodeId);
  const messagesArray = ydoc.getArray<CanvasChatMessage>(chatArrayKey);

  // Memoize store functions to prevent infinite loops with useSyncExternalStore
  // The subscribe and getSnapshot functions must be stable references
  const store = useMemo(() => {
    // Cache the last snapshot to return stable references
    let cachedArray: CanvasChatMessage[] = messagesArray.toArray();

    return {
      subscribe: (callback: () => void) => {
        const observer = () => {
          cachedArray = messagesArray.toArray();
          callback();
        };
        messagesArray.observe(observer);
        return () => messagesArray.unobserve(observer);
      },
      getSnapshot: () => cachedArray,
    };
  }, [messagesArray]);

  // Subscribe to Yjs array changes using useSyncExternalStore
  const messages = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);

  // Use output node utilities
  const {
    isOutputConnected,
    getConnectedBlockId,
    setBlockContent,
    clearBlock,
  } = useOutputNode({
    sourceNodeId: nodeId,
    nodesMap,
    edgesMap,
    ydoc,
    makeChange,
  });

  // Check if connected
  const outputConnected = isOutputConnected();

  /**
   * Add a message to the Yjs array
   */
  const addMessage = useCallback(
    (message: CanvasChatMessage) => {
      makeChange(() => {
        messagesArray.push([message]);
      });
    },
    [messagesArray, makeChange]
  );

  /**
   * Update a message in the Yjs array
   * IMPORTANT: Reads directly from Yjs array inside transaction to avoid stale React state
   */
  const updateMessage = useCallback(
    (messageId: string, update: Partial<CanvasChatMessage>) => {
      makeChange(() => {
        // Read directly from Yjs to get fresh data (React state may be stale during streaming)
        const currentMessages = messagesArray.toArray();
        const index = currentMessages.findIndex((m) => m.id === messageId);
        if (index !== -1) {
          const existing = currentMessages[index];
          messagesArray.delete(index, 1);
          messagesArray.insert(index, [{ ...existing, ...update }]);
        }
      });
    },
    [messagesArray, makeChange]
  );

  /**
   * Send a message and get AI response
   * @param content - Text content of the message
   * @param images - Optional array of image attachments
   */
  const sendMessage = useCallback(
    async (content: string, images?: ChatImageAttachment[]) => {
      // Allow sending if there's text OR images
      if (!content.trim() && (!images || images.length === 0)) return;

      console.log('[ChatNode] Sending message:', { content, imageCount: images?.length || 0 });

      // Create user message (with images if provided)
      const userMessage = createChatMessage('user', content, images);
      addMessage(userMessage);

      // Create placeholder for assistant response
      const assistantMessage = createChatMessage('assistant', '');
      addMessage(assistantMessage);

      setStatus('streaming');
      setError(null);

      // Clear connected block if any (prepare for streaming)
      const connectedBlockId = getConnectedBlockId();
      if (connectedBlockId) {
        clearBlock(connectedBlockId);
      }

      try {
        // Create abort controller for cancellation
        abortControllerRef.current = new AbortController();

        // Build message history for context (include images in the format the API expects)
        const messageHistory = messages.map((m) => ({
          role: m.role,
          content: m.content,
          // Include images if present
          images: m.images?.map((img) => ({
            mimeType: img.mimeType,
            base64: img.base64,
          })),
        }));

        // Add current message with images
        messageHistory.push({
          role: 'user',
          content,
          images: images?.map((img) => ({
            mimeType: img.mimeType,
            base64: img.base64,
          })),
        });

        console.log('[ChatNode] Sending to API:', `/api/boards/${boardId}/chat`);

        // Send request to chat API with model settings
        const response = await fetch(`/api/boards/${boardId}/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            messages: messageHistory,
            taggedAgents: [], // No agent tagging for canvas chat
            // Pass model settings to backend
            modelSettings: modelSettings
              ? {
                  modelId: modelSettings.modelId,
                  systemInstructions: modelSettings.systemInstructions,
                  temperature: modelSettings.temperature,
                }
              : undefined,
          }),
          signal: abortControllerRef.current.signal,
        });

        console.log('[ChatNode] Response status:', response.status, response.statusText);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('[ChatNode] Response error:', errorText);
          throw new Error(`Chat request failed: ${response.statusText} - ${errorText}`);
        }

        if (!response.body) {
          throw new Error('No response body');
        }

        // Read streaming response
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let accumulatedContent = '';
        // Buffer for incomplete SSE lines (chunks can split across reads)
        let sseBuffer = '';

        console.log('[ChatNode] Starting to read stream...');

        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            console.log('[ChatNode] Stream done, total content length:', accumulatedContent.length);
            break;
          }

          // Decode chunk and add to buffer
          sseBuffer += decoder.decode(value, { stream: true });

          // Split by newlines, keeping incomplete last line in buffer
          const lines = sseBuffer.split('\n');
          // Last element might be incomplete - keep it in buffer
          sseBuffer = lines.pop() || '';

          for (const line of lines) {
            // Skip empty lines (SSE uses double newline as separator)
            if (!line.trim()) continue;

            // SSE format: "data: {json}"
            if (line.startsWith('data: ')) {
              const data = line.slice(6);

              // Stream complete signal
              if (data === '[DONE]') {
                console.log('[ChatNode] Received [DONE] signal');
                continue;
              }

              try {
                const parsed = JSON.parse(data);

                // Handle text content
                if (parsed.type === 'text') {
                  accumulatedContent += parsed.content;

                  // Update message in Yjs
                  updateMessage(assistantMessage.id, { content: accumulatedContent });

                  // Also stream to connected block if any
                  if (connectedBlockId) {
                    const htmlContent = accumulatedContent
                      .split('\n\n')
                      .map((para: string) => `<p>${para.replace(/\n/g, '<br>')}</p>`)
                      .join('');
                    setBlockContent(connectedBlockId, htmlContent);
                  }
                } else if (parsed.type === 'error') {
                  console.error('[ChatNode] Received error from API:', parsed.content);
                  throw new Error(parsed.content);
                }
              } catch (parseErr) {
                // JSON parse error - shouldn't happen with proper buffering, but log for debugging
                if (parseErr instanceof SyntaxError) {
                  console.warn('[ChatNode] JSON parse error:', data.substring(0, 100));
                } else {
                  throw parseErr;
                }
              }
            }
          }
        }

        // If we got no content, log a warning
        if (!accumulatedContent) {
          console.warn('[ChatNode] Stream completed but no content received');
        }

        setStatus('idle');
      } catch (err) {
        // Check if request was aborted
        if (err instanceof Error && err.name === 'AbortError') {
          console.log('[ChatNode] Request aborted');
          setStatus('idle');
          return;
        }

        console.error('[ChatNode] Error:', err);
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        setStatus('error');

        // Update assistant message with error content
        updateMessage(assistantMessage.id, { content: `Error: ${errorMessage}` });
      } finally {
        abortControllerRef.current = null;
      }
    },
    [
      boardId,
      messages,
      addMessage,
      updateMessage,
      getConnectedBlockId,
      clearBlock,
      setBlockContent,
      modelSettings,
    ]
  );

  /**
   * Pin a message to canvas as a new BlockNode
   */
  const pinMessage = useCallback(
    (messageId: string) => {
      const message = messages.find((m) => m.id === messageId);
      if (!message || message.isPinned) return;

      // Get node position for placing the BlockNode
      const currentNode = nodesMap.get(nodeId);
      const position = currentNode?.position || { x: 0, y: 0 };

      // Create a BlockNode with the message content
      const htmlContent = message.content
        .split('\n\n')
        .map((para) => `<p>${para.replace(/\n/g, '<br>')}</p>`)
        .join('');

      createOutputNode({
        sourceNodeId: nodeId,
        sourceNodePosition: position,
        title: message.role === 'assistant' ? 'AI Response' : 'Message',
        content: htmlContent,
        nodesMap,
        edgesMap,
        makeChange,
      });

      // Mark message as pinned
      updateMessage(messageId, { isPinned: true });
    },
    [messages, nodeId, nodesMap, edgesMap, makeChange, updateMessage]
  );

  /**
   * Clear all chat history
   */
  const clearChat = useCallback(() => {
    makeChange(() => {
      messagesArray.delete(0, messagesArray.length);
    });
    setError(null);
  }, [messagesArray, makeChange]);

  /**
   * Stop current streaming response
   */
  const stop = useCallback(() => {
    abortControllerRef.current?.abort();
    setStatus('idle');
  }, []);

  return {
    messages,
    status,
    error,
    isOutputConnected: outputConnected,
    sendMessage,
    pinMessage,
    clearChat,
    stop,
  };
}
